import { action, computed, flow, makeObservable, observable } from 'mobx'
import Store from './Store'
import Paginator from '../models/Paginator'
import UserService from '../services/UserService'
import User from '../models/User'
import Equipment from '../models/Equipment'
import Role from '../models/Role'
import clone from '../utils/clone'

class UsersStore extends Store {
  /**
   * @var {User}
   */
  @observable user = new User()
  backupUser = {}
  /**
   * @var {array<User>}
   */
  @observable users = []
  /**
   * @var {array<Equipment>}
   */
  @observable equipments = []
  /**
   * @var {array<Role>}
   */
  @observable roles = []

  @observable paging = {}

  @computed
  get isDirty () {
    return this.user.isDirty
  }

  constructor (loading = true) {
    super()
    makeObservable(this)
    this.isLoading = loading
  }

  @action
  newUser () {
    this.user = new User()
    this.user.setOriginalData({})
  }

  @flow
  * loadAll () {
    this.isLoading = true
    const result = yield UserService.getUsers(this.query)
    this.paging.Users = new Paginator(result.paginator.Users)
    this.users = []
    for (const user of result.users) {
      const userObj = new User()
      userObj.setOriginalData(user)
      this.users.push(userObj)
    }
    this.isLoading = false
  }

  @flow
  * loadOne (userId) {
    this.isLoading = true
    const result = yield UserService.getUser(userId)
    this.equipments = []
    this.roles = []
    if (result) {
      this.backupUser = clone(result.user)
      this.user = new User()
      this.user.setOriginalData(result.user)
      for (const equipment of result.equipments) {
        const equipmentObj = new Equipment()
        equipmentObj.setOriginalData(equipment)
        this.equipments.push(equipmentObj)
      }
      for (const role of result.roles) {
        this.roles.push(new Role(role))
      }
    }
    this.isLoading = false
  }

  @flow
  * loadDeps () {
    this.isLoading = true
    const result = yield UserService.getDeps()
    this.equipments = []
    this.roles = []
    if (result) {
      for (const equipment of result.equipments) {
        const equipmentObj = new Equipment()
        equipmentObj.setOriginalData(equipment)
        this.equipments.push(equipmentObj)
      }
      for (const role of result.roles) {
        this.roles.push(new Role(role))
      }
    }
    this.isLoading = false
  }

  @flow
  * getUsersByEquipmentId (equipmentId) {
    this.isLoading = true
    const result = yield UserService.getUsersByEquipmentId(equipmentId)
    this.users = []
    for (const user of result.users) {
      const userObj = new User()
      userObj.setOriginalData(user)
      this.users.push(userObj)
    }
    this.isLoading = false
  }

  @flow
  * getInspectors () {
    this.isLoading = true
    const result = yield UserService.getInspectors()
    this.users = []
    for (const user of result.users) {
      const userObj = new User()
      userObj.setOriginalData(user)
      this.users.push(userObj)
    }
    this.isLoading = false
  }

  @action
  revert () {
    this.user = new User()
    this.user.setOriginalData(this.backupUser)
  }

  @flow
  * save (userId) {
    this.isSaving = true
    try {
      let response
      if (userId) {
        response = yield UserService.saveUser(userId, this.user.toJson())
      } else {
        response = yield UserService.createUser(this.user.toJson())
      }

      this.isSaving = false
      if (response.result.success) {
        this.backupUser = clone(response.result.user)
        this.user.setOriginalData(response.result.user)
        return response.result
      }

      if (response.result.message) {
        this.addToast(response.result.message, response.result.success ? 'success' : 'error')
      }
      if (response.errorMessages) {
        this.errorMessages = { user: response.errorMessages }
      }
      return response.result
    } catch (e) {
      this.addToast('Impossible de rejoindre le serveur en ce moment. Veuillez réessayer', 'error')
    }
  }

  @flow
  * deleteUser (userId) {
    let response
    try {
      response = yield UserService.deleteUser(userId)
    } catch (e) {
      this.addToast("Impossible de supprimer l'utilisateur", 'error')
      throw e
    }
    if (response.result.success) {
      this.addToast("L'utilisateur à été supprimé", 'success', 10000)
    } else {
      this.addToast(response.result.message, 'error')
      throw new Error(response.result.message)
    }
  }

  @action
  changePage (newPage) {
    if (newPage <= this.paging.Users.pageCount && newPage > 0) {
      this.paging.Users.page = newPage
      this.query.page = newPage
    }
  }

  @action
  clearList () {
    this.users = []
  }
}

export default UsersStore
